/**
 * nj-model — the unified Journey document model, page-type registry, block
 * schemas and the derivation rules that turn the tenant's REAL objects
 * (queues, events, the appointment catalogue) into journey documents.
 *
 * Ported from the Gold Master prototype `New Journeys.dc.html` (phases 2–5).
 * Registry ids, labels, colors, glyphs, seed copy, toggles, block schemas and
 * invariant wording are the prototype's, verbatim — the operator ruled the
 * prototype's UX ships exactly as designed.
 *
 * Nothing in here fabricates tenant content: every name, service, ticket and
 * date on a derived document comes from the object it was derived from.
 */

/* ── Step navigation ──────────────────────────────────────────────────────
 * How a customer moves through a journey's pages (operator rulings,
 * 2026-09-02). DERIVED, never authored: a footer's presence follows from the
 * page's position and from where the journey COMMITS, so an author cannot
 * assemble a flow whose navigation contradicts its own shape.
 *
 * THE COMMIT IS THE HINGE
 * Before the booking exists, going back is free and reversible. After it
 * exists, Back is either a re-submit or a silent orphan — so there is no Back,
 * and no Next either: the journey is over. Changing a made booking is a
 * different act on a different object (the booking, not the flow), which is why
 * nothing on a post-commit page pretends to be a step.
 *
 * The commit page is the LAST page before the first terminal one, so it is the
 * page whose forward action creates the booking — its button says what that act
 * is ("Join the queue", "Book", "Register") rather than "Next".
 */

/** Pages that only exist AFTER the booking does. Terminal by nature. */
export const POST_COMMIT_PAGES: string[] = ["waiting", "noservice", "csat", "confirmation", "apptconfirm"];
export const isPostCommitPage = (id: string): boolean => POST_COMMIT_PAGES.indexOf(id) > -1;

/** What the committing button says, per journey type. The default only — Setup
 *  overrides it for the journey, and the commit page can override that. */
export const COMMIT_LABEL_BY_TYPE: Record<string, string> = {
  Queue: "Join the queue",
  Appointment: "Book",
  Event: "Register",
};

/** Index of the page whose forward action creates the booking, or -1 if the
 *  journey has no pre-commit page at all (a single terminal page). */
export function commitPageIndex(pages: { id: string }[]): number {
  const first = pages.findIndex((pg) => isPostCommitPage(pg.id));
  return first === -1 ? pages.length - 1 : first - 1;
}

export interface StepNav {
  /** Show Back. Never on the first page — nothing is behind it. */
  back: boolean;
  /** Show the forward button. */
  next: boolean;
  /** Its label: "Next", or the commit wording on the committing page. */
  label: string;
  isCommit: boolean;
  /** This page is after the commit: no footer, and its own actions read as
   *  links rather than buttons. */
  postCommit: boolean;
}

export function stepNavFor(
  doc: { pages: { id: string }[]; type?: string; commitLabel?: string },
  idx: number,
): StepNav {
  const pages = doc.pages ?? [];
  const pg = pages[idx];
  const none: StepNav = { back: false, next: false, label: "", isCommit: false, postCommit: false };
  if (!pg) return none;
  const firstPost = pages.findIndex((x) => isPostCommitPage(x.id));
  /* Anything at or after the first terminal page is post-commit, including an
     ordinary content page an author drops in behind one: the booking exists by
     then whatever the page's type says. */
  const post = isPostCommitPage(pg.id) || (firstPost > -1 && idx > firstPost);
  if (post) return { ...none, postCommit: true };
  const commit = commitPageIndex(pages);
  const isCommit = commit >= 0 && idx === commit;
  const label = isCommit
    ? String((pg as { commitLabel?: string }).commitLabel || doc.commitLabel || COMMIT_LABEL_BY_TYPE[doc.type ?? ""] || "Confirm")
    : "Next";
  return { back: idx > 0, next: true, label, isCommit, postCommit: false };
}

export interface Toggle { k: string; label: string; on: boolean }
export interface Block { uid: string; type: string; text?: string; [k: string]: unknown }
export interface NJPage {
  id: string; uid?: string; label: string; color: string; tint: string; core: string | null;
  h: string; s: string; toggles: Toggle[]; content: Block[];
  /** Confirmation pages only (2026-08-31). The page's sections in render order,
   *  each carrying its own `hidden` — array position IS the order, so there is
   *  no second list to disagree with it (shape agreed with the API line,
   *  2026-09-01). Sections are not blocks: a block holds copy an author typed, a
   *  section renders data that only exists at runtime — the booked service, the
   *  reference, the answers given. See confirmation-sections.ts. */
  sections?: CSState;
  /** This page's own wording for the committing button, when it is the commit
   *  page. Empty = the journey default. Step navigation, 2026-09-02. */
  commitLabel?: string;
  /** Fields the sections read, edited in the inspector. */
  bannerTitle?: string;
  cancelHours?: string;
  note?: string;
  /** The Feedback section's prompt. Added 2026-09-08 to close a silent drop: the
   *  inspector's `onPatch` is typed `CSPageFields`, which carries this field, so
   *  a patch containing it was already being handed to `patchPage` — and
   *  `Partial<NJPage>` had nowhere to put it, so it type-errored on their config
   *  and would have been discarded on ours. The canvas already reads it
   *  (`f.surveyPrompt || "How did it go?"`); this is where it now persists. */
  surveyPrompt?: string;
  /** Services this page offers — ids/names resolved against the page's catalogue. */
  svc?: string[];
  /**
   * One-Stop registry blocks, in page order. Only ever set on an `onestop` page.
   *
   * Separate from `content` deliberately, exactly as `sections` is on a
   * confirmation page: these are typed by `ONE_STOP_BLOCK_TYPES` and carry a
   * `config` the contract validates, where `content` holds the designer's own
   * copy blocks with their own palette and defaults. One array cannot be both
   * without `blocksFor()` and the palette's "already used" test lying about the
   * other. Undo, autosave and preview cover this field like any other, because
   * it is part of the document.
   */
  osBlocks?: { uid: string; id: string; type: string; version: number; config: Record<string, unknown>; visibility?: "always" | "signed-in" | "signed-out" }[];
  /** Canvas width the author last worked at, so the designer reopens the way it
   *  was left. On the PAGE rather than in session state: it rides the document's
   *  own autosave and survives a reload, which is the whole point of it. */
  viewport?: "desktop" | "mobile";
  /** One-Stop page settings — the half that lands on the document row. */
  osSettings?: { slug?: string; navLabel?: string | null; locale?: string; supportedLocales?: string[]; timeZone?: string; journeyTarget?: "tab" | "same" };
  [k: string]: unknown;
}
/**
 * A journey document's flow step. LEGACY — kept only as the input shape
 * `njFlowOf` migrates from (nj-flow-migrate.ts). The document now stores the
 * builder's own tree in `Doc.flow`; nothing writes this shape any more.
 * @deprecated read-only, for stored documents predating 2026-08-31.
 */
export interface Step {
  id: string; kind: string; title?: string; sub?: string; page?: number;
  chips?: string[]; promo?: boolean; messageId?: string | null; surveyId?: string | null; groupId?: string | null;
  event?: string;
  amount?: number; unit?: string; timing?: string;
  condition?: string; outcome?: string; sendWhen?: string;
}
import { ownerAudit, type JourneyAudit } from "./nj-audit";
import { blocksForTarget, isContainer } from "../shared/block-registry";
import { csDefaultState, csTypeOf, type CSState } from "./confirmation-sections";
import {
  qfMkPage, qfMkComms, qfMkSurvey, qfMkWait, qfMkDecision, qfMkExit,
  type QFFlow, type QFStep,
} from "./flow/jf-model";

/** Where a derived document came from — the object that owns the journey. */
export interface Origin {
  kind: "queue" | "event" | "appointment" | "board" | "onestop" | "local";
  id: string;
  label: string;
  /** Route + params for "open the owning object". */
  route?: string;
  params?: Record<string, string>;
  /** Public slug, when the object has one (availability is slug-scoped). */
  slug?: string | null;
}
export interface Attach {
  groupId?: string | null;   // booking questions (question group)
  surveyId?: string | null;  // survey
  ticketGroupId?: string | null;
  promoId?: string | null;
}
/** Publishing settings that used to live on the (now-deleted) landing page type.
 *  Journey-level per the entry-page ruling (build plan, decision 1). */
export interface PublishSettings {
  slug?: string; tenantHost?: string; seoTitle?: string; shareImage?: string;
  theme?: string; font?: string; customCss?: string;
  /** The landing_pages row this journey's pages publish into (nj-attach
   *  publishJourneyPages). Remembered so the second publish UPDATES the public
   *  page rather than minting a second one for the same event. */
  landingPageId?: string | null;
}
export interface Doc {
  name: string; type?: string; status: string; updated: string; sub: string; edited: boolean;
  pages: NJPage[];
  /** The journey's flow, as ONE TREE (2026-08-31). Replaces `flowMain` plus
   *  `flowYes` / `flowNo`, three flat lists that between them could express
   *  exactly one decision with exactly two branches — so a second decision was
   *  flattened on save and a third path had nowhere to live. Stored documents in
   *  the old shape are converted on load by `njFlowOf`. */
  flow: QFFlow;
  origin?: Origin; attach?: Attach;
  /** Setup values for a journey with no owning object YET (created from a
   *  template). Held here so they survive reload. NOTE: there is no
   *  `local:` → `event:` attach step yet, so these values stay here and do
   *  NOT reach an event record — the Setup panel and Publish view say so. */
  setup?: Partial<EventLike>;
  /** Same holding store for a QUEUE journey with no queue record yet, and for
   *  a display board's configuration before it has been saved to the boards
   *  library. Both mirror onto their real record the moment one exists. */
  qsetup?: Partial<QueueConfigLike>;
  boardCfg?: Partial<BoardConfigLike>;
  /** Appointment funnel configuration. Unlike qsetup and boardCfg, this is NOT
   *  a holding bag waiting for a record — there is no appointment record to
   *  mirror onto (APPOINTMENT_BINDING.ownsRecord: false), so this IS the store.
   *  Everything here is presentation and selection the funnel owns; anything
   *  service-scoped lives on the catalogue row and is referenced, never copied.
   *  An appointment record is requested — see
   *  CD-API-REQUEST-service-scoped-attachments-2026-08-31.md §8. */
  apptSetup?: Partial<ApptSetupLike>;
  /** uid of the page a stranger lands on. Exactly one page per journey; any
   *  content page is eligible. Absent = the first page, unchanged from today. */
  entryUid?: string | null;
  publish?: PublishSettings;
  /** Who created this journey and who changed it last. Minted by NewJourneys'
   *  `saveDoc` on every write (`from: "designer"`); on a derived row the
   *  designer has never saved, `docFrom*` falls back to the OWNING record's own
   *  created/modified fields (`from: "owner"`). See nj-audit. */
  audit?: JourneyAudit;
  /** Step navigation (2026-09-02). The journey's default wording for the button
   *  that creates the booking — the commit page can override it, and both fall
   *  back to COMMIT_LABEL_BY_TYPE. */
  commitLabel?: string;
  /** Jump to the next page the moment a choice is made. OFF by default: Next is
   *  the way forward, and a page that advances on its own takes the choice back
   *  before the customer has finished making it. */
  autoAdvance?: boolean;
}
export interface ExpRow { doc: string; scope: string }
/**
 * LEGACY as of 2026-09-01 (One-Stop stage 2), kept for the migration only.
 *
 * `anchor` + `attached[]` was the anchor-and-attach experience model. A One-Stop
 * journey is now an experience DOCUMENT — slug, header, ordered blocks — and
 * attachment is a block's `scope`, not a row here. `onestop-page-api.ts`
 * (`expToOneStopDocument`) converts a stored `Exp` into that document; nothing
 * new should read these two fields, and nothing at all should write them.
 *
 * Being an Experience survives as a PROPERTY of a journey — that is where the
 * single-registration flag lands (stage 7) — so what retires here is this
 * record shape, not the concept.
 */
export interface Exp { name: string; status: string; updated: string; anchor: string | null; attached: ExpRow[] }

/* ── Page-type registry ─────────────────────────────────────────────── */
export interface RegEntry {
  id: string; label: string; glyph: string; color: string; tint: string; core: string | null;
  phase?: string; desc: string; seed: { h: string; s: string; toggles: Toggle[] };
}
export const REG: RegEntry[] = [
  { id: "services", label: "Services", glyph: "◰", color: "#2D60EB", tint: "#E8EEFD", core: "Pick a service", desc: "Service picker with wait estimates", seed: { h: "What do you need help with?", s: "Pick a service — we'll estimate your wait.", toggles: [{ k: "est", label: "Show wait estimates", on: true }, { k: "grid", label: "Grid layout", on: false }] } },
  { id: "wait", label: "Wait Time", glyph: "◷", color: "#C8861E", tint: "#FAF1DE", core: "Wait estimate", desc: "Live wait for the chosen service", seed: { h: "Here's the current wait", s: "Walk-in service", toggles: [{ k: "book", label: "Offer 'book instead' link", on: true }] } },
  { id: "contact", label: "Contact Details", glyph: "✍", color: "#7C3AED", tint: "#F1EAFD", core: "Contact + questions", desc: "Contact form + attached booking questions", seed: { h: "Almost there", s: "We'll text you when it's your turn.", toggles: [{ k: "party", label: "Ask party size", on: false }, { k: "sms", label: "SMS updates consent", on: true }] } },
  { id: "waiting", label: "Waiting in queue", glyph: "☰", color: "#1F8A5B", tint: "#E4F5EF", core: "Live position", desc: "Live position with host messaging", seed: { h: "You're in the line", s: "Keep this page open — we'll call you here.", toggles: [{ k: "pos", label: "Show position", on: true }, { k: "msg", label: "Allow host messaging", on: true }] } },
  /* Queue pages the queue editor owned, brought into the registry with the
   * port (2026-08-24) so a queue journey's optional and outcome pages exist
   * here as first-class page types — labels and subs are qp-meta's. */
  { id: "prebook", label: "Pre-booking", glyph: "✱", color: "#DB2777", tint: "#FDEBF3", core: null, desc: "Optional — questions and content before joining", seed: { h: "Before you join", s: "A couple of questions so we can help faster.", toggles: [{ k: "skip", label: "Allow skipping", on: true }] } },
  { id: "locations", label: "Pick a location", glyph: "◉", color: "#0E7490", tint: "#E0F2F7", core: "Location picker", desc: "Optional — compare wait times across locations", seed: { h: "Where would you like to be seen?", s: "Live waits across your nearby locations.", toggles: [{ k: "waits", label: "Show live waits", on: true }, { k: "map", label: "Show map", on: false }] } },
  { id: "noservice", label: "Left without service", glyph: "◼", color: "#C0392B", tint: "#FBEAE7", core: null, desc: "Shown when a customer leaves before being served", seed: { h: "Sorry we missed you", s: "Come back any time — or book a slot that suits you.", toggles: [{ k: "rebook", label: "Show rebook link", on: true }] } },
  { id: "csat", label: "How did we do?", glyph: "★", color: "#0E8A6A", tint: "#E6F5F1", core: "Survey + thanks", desc: "Attached survey and thanks", seed: { h: "Thanks for visiting!", s: "Two minutes of feedback helps the team.", toggles: [{ k: "review", label: "Show review link", on: false }] } },
  { id: "content", label: "Content", glyph: "▤", color: "#697180", tint: "#F4F5F7", core: null, desc: "Blocks only — compose freely", seed: { h: "New page", s: "", toggles: [] } },
  /* The One-Stop page (2026-09-01). Its content is not the designer's block
     vocabulary but the One-Stop BLOCK REGISTRY — queues, appointments, events,
     search, identity — held in `osBlocks` for the same reason a confirmation
     page holds `sections`: they are the page, they are typed by a contract, and
     they are not interchangeable with the copy blocks the palette offers. The
     ordinary `content` array still works alongside them. */
  { id: "onestop", label: "One-Stop page", glyph: "◈", color: "#262265", tint: "#E4E1F7", core: null, desc: "The desks, services and events a student can reach in one visit", seed: { h: "One-Stop", s: "Everything you need, in one place.", toggles: [] } },
  { id: "apptsvc", label: "Book a service", glyph: "◔", color: "#0AA396", tint: "#E0F2F1", core: "Service picker", phase: "PHASE 3", desc: "Appointment services with durations", seed: { h: "What would you like to book?", s: "Pick a service — then choose a time.", toggles: [{ k: "dur", label: "Show durations", on: true }, { k: "dept", label: "Group by department", on: false }] } },
  { id: "appttime", label: "Pick a time", glyph: "▦", color: "#0AA396", tint: "#E0F2F1", core: "Time picker", phase: "PHASE 3", desc: "Live availability slot picker", seed: { h: "Pick a time", s: "Times shown in your local timezone.", toggles: [{ k: "adv", label: "Show advisor names", on: true }, { k: "soon", label: "Suggest soonest slot", on: false }] } },
  { id: "apptconfirm", label: "Confirmation", glyph: "✓", color: "#0AA396", tint: "#E0F2F1", core: "Booking summary", phase: "PHASE 3", desc: "Summary, calendar, reschedule", seed: { h: "You're booked!", s: "A confirmation is on its way to your inbox.", toggles: [{ k: "cal", label: "Add-to-calendar button", on: true }, { k: "resched", label: "Show reschedule link", on: true }] } },
  // "eventlanding" removed (build plan decision 1, stage 1): a landing page was never a
  // distinct type — it was the public entry point (now Doc.entryUid + Doc.publish, journey-
  // level) plus a content page (no core) plus the date/venue line (now the "eventdetails"
  // block below). Any content page can be flagged as a journey's entry.
  { id: "register", label: "Register", glyph: "✎", color: "#D61A55", tint: "#FDEBF3", core: "Registration + questions", phase: "PHASE 4", desc: "Registration form + attached questions", seed: { h: "Save your spot", s: "Registration takes a minute.", toggles: [{ k: "guest", label: "Allow +1 guests", on: true }] } },
  // "wl" (waitlist when full) removed from page toggles (parity audit, stage 0): waitlist is
  // event-record capacity data, not page config. Belongs in Setup · Tickets once that view exists (stage 3).
  { id: "tickets", label: "Tickets", glyph: "⬧", color: "#D61A55", tint: "#FDEBF3", core: "Ticket picker", phase: "PHASE 4", desc: "Ticket groups from the library", seed: { h: "Choose your tickets", s: "Checkout is secure.", toggles: [{ k: "sold", label: "Show remaining count", on: true }, { k: "promo2", label: "Promo code entry", on: false }] } },
  /** Display board — a lobby TV, not a customer page: no hero, no blocks; its
   *  content is the board's own block vocabulary (board-blocks.tsx), edited in
   *  Setup · Configuration. */
  { id: "board", label: "Display Board", glyph: "▣", color: "#2D60EB", tint: "#E8EEFD", core: "Board content", phase: "PHASE 6", desc: "Lobby display — live queue state on a TV", seed: { h: "Now serving", s: "Live from the queues this board shows.", toggles: [{ k: "clock", label: "Show the clock", on: true }, { k: "org", label: "Show the organisation name", on: true }] } },
  /** Every journey ends on one of these (2026-08-31). Replaces the tenant-level
   *  confirmation-pages library: branding is the journey's, services arrive with
   *  the booking, and the sections a given journey may use come from its type.
   *  `apptconfirm` predates this and stays for appointment journeys already
   *  carrying it; both read the same section registry. */
  { id: "confirmation", label: "Confirmation", glyph: "✓", color: "#1D8348", tint: "#E3F5E9", core: "Confirmation sections", phase: "PHASE 7", desc: "What the customer sees once it's done", seed: { h: "You're all set", s: "", toggles: [] } },
  { id: "exphub", label: "Experience hub", glyph: "◈", color: "#262265", tint: "#E4E1F7", core: "Live journey list", phase: "PHASE 5", desc: "After check-in — every journey inside this experience", seed: { h: "You're checked in", s: "Everything at this event, in one place.", toggles: [{ k: "waits", label: "Show live waits & slots", on: true }, { k: "badge", label: "Show check-in badge", on: true }] } },
];
/** Unknown ids fall back to the plain Content page — looked up by id, never by
 *  index (the registry grows). */
export const regOf = (id: string): RegEntry => REG.find((r) => r.id === id) ?? REG.find((r) => r.id === "content")!;

/* ── Nested content (stage 4) ────────────────────────────────────────
 * A container block keeps its children in `cells: Block[][]`, so a page's
 * content is a shallow TREE rather than a flat list. These three are the only
 * places that know: every caller that used to map or find over `page.content`
 * goes through them instead, or a block inside a column silently stops
 * responding to selection, config edits and deletion. */
export const blockCells = (b: Block): Block[][] | null =>
  (isContainer(b.type) && Array.isArray(b.cells) ? (b.cells as Block[][]) : null);

/** Map every block, nested ones included. Return null to drop a block. */
export function mapContent(list: Block[], fn: (b: Block) => Block | null): Block[] {
  const out: Block[] = [];
  (list ?? []).forEach((b) => {
    const cells = blockCells(b);
    const withCells = cells ? { ...b, cells: cells.map((cell) => mapContent(cell, fn)) } : b;
    const next = fn(withCells);
    if (next) out.push(next);
  });
  return out;
}

export function findContentBlock(list: Block[], uid: string): Block | null {
  for (const b of (list ?? [])) {
    if (b.uid === uid) return b;
    const cells = blockCells(b);
    if (cells) { for (const cell of cells) { const hit = findContentBlock(cell, uid); if (hit) return hit; } }
  }
  return null;
}

/** Append a block into one cell of one container. */
export function addToCell(list: Block[], containerUid: string, cellIndex: number, block: Block): Block[] {
  return list.map((b) => {
    if (b.uid !== containerUid) {
      const cells = blockCells(b);
      return cells ? { ...b, cells: cells.map((cell) => addToCell(cell, containerUid, cellIndex, block)) } : b;
    }
    const cells = blockCells(b) ?? [[], []];
    return { ...b, cells: cells.map((cell, i) => (i === cellIndex ? [...cell, block] : cell)) };
  });
}

/* Core-block invariants — enforced, not advised. */
export const INV: Record<string, string> = {
  confirmation: "Every journey ends on a confirmation page — there is no tenant-level default behind it any more.",
  /* `apptconfirm` appeared TWICE in this object (fixed 2026-09-08) — here with
     `confirmation`'s wording, and again below with its own. The second silently
     won at runtime, so removing this one changes nothing and removing the other
     would have changed the message. The booking-specific line is the keeper. */
  contact: "Bookings and queue joins can't complete without Contact details.",
  waiting: "A queue journey must keep its Waiting in queue page — it's where the customer is called.",
  apptsvc: "The booking funnel needs its Book a service page.",
  appttime: "A booking can't complete without a Pick a time page.",
  apptconfirm: "The booking funnel must end in a Confirmation page.",
  register: "An event can't take registrations without the Register page.",
  tickets: "A ticketed event can't sell tickets without the Tickets page.",
  board: "A display-board journey must keep its Display Board page — it's what the lobby screen renders.",
};

export const KIND_META: Record<string, { glyph: string; tint: string; color: string; label: string; field?: string }> = {
  trigger: { glyph: "⚡", tint: "#F4F5F7", color: "#1F2430", label: "Trigger", field: "Starts when" },
  page: { glyph: "▤", tint: "#EDE5FE", color: "#5142C7", label: "Page step" },
  comms: { glyph: "✉", tint: "#ECF1FE", color: "#2D60EB", label: "Message step" },
  wait: { glyph: "◷", tint: "#FFFAED", color: "#916D0F", label: "Wait step", field: "Duration" },
  decision: { glyph: "⑂", tint: "#FFFAED", color: "#916D0F", label: "Decision step", field: "Condition" },
  survey: { glyph: "✎", tint: "#E6F4EE", color: "#027D47", label: "Survey step" },
  questions: { glyph: "?", tint: "#F1EAFD", color: "#7C3AED", label: "Questions step" },
  exit: { glyph: "◼", tint: "#F4F5F7", color: "#697180", label: "Exit", field: "Outcome" },
};

/** Library each attachment block edits, and the picker it opens. */
export const ATTACH_TYPES: Record<string, string> = { qgroup: "questions", survey: "survey", promo: "promo", ticketgrp: "tickets" };
/** Page type → the attachment block seeded onto it. */
export const ATTACH_FOR: Record<string, string> = { contact: "qgroup", register: "qgroup", csat: "survey", tickets: "ticketgrp" };

export const LIB_META: Record<string, { title: string; lib: string; glyph: string; tint: string; color: string; nameLabel: string; route: string }> = {
  questions: { title: "Booking questions", lib: "Questions library", glyph: "?", tint: "#F1EAFD", color: "#7C3AED", nameLabel: "Question group", route: "questions.groups" },
  survey: { title: "Survey", lib: "Surveys library", glyph: "✎", tint: "#E6F4EE", color: "#027D47", nameLabel: "Survey", route: "surveys" },
  promo: { title: "Coupon & promo", lib: "Promos library", glyph: "⌾", tint: "#FFFAED", color: "#916D0F", nameLabel: "Promo", route: "promos" },
  message: { title: "Message", lib: "Message library", glyph: "✉", tint: "#ECF1FE", color: "#2D60EB", nameLabel: "Message", route: "messaging" },
  tickets: { title: "Ticket group", lib: "Tickets library", glyph: "⬧", tint: "#FDEBF3", color: "#D61A55", nameLabel: "Ticket group", route: "ticket-groups" },
};

/** The page palette, derived from the ONE block registry (stage 4). The list is
 *  no longer written out here: a block declares which targets it supports and
 *  this reads the "page" slice, so the email palette and this one cannot drift.
 *  Journey pages gained `columns` in that merge — as a real container, per the
 *  operator's ruling — and deliberately did NOT gain `heading`, because a page
 *  already carries its own title and subtitle. */
export const PALETTE: { type: string; label: string; repeat?: boolean }[] =
  blocksForTarget("page").map((b) => ({ type: b.type, label: b.label, ...(b.repeat ? { repeat: true } : {}) }));
/** Pages with a core block (their own transactional content up top). */
export const CORE_BEARING: Set<string> = new Set(REG.filter((r) => r.core).map((r) => r.id));
/** Every block is available on every page (operator 2026-08-24) — hero included,
 *  on core-bearing pages too: it sits above the core, which is where a page's
 *  branding belongs. The display board is the one exception: its content is the
 *  board block vocabulary, not journey-page blocks. */
export function blocksFor(pageId: string): typeof PALETTE {
  return pageId === "board" ? [] : PALETTE;
}
export const CDEF: Record<string, string> = {
  text: "Add your copy here — this text block sits around the core.",
  notice: "Something customers should know before joining.",
  button: "Button label", divider: "", spacer: "", image: "", hero: "Everything for your visit",
  video: "", gallery: "", map: "", faq: "How long will my visit take?",
  quote: "In and out in ten minutes — booked from my phone.", stats: "", countdown: "", social: "",
};

export const NETS: [string, string, string][] = [
  ["x", "X", "✕"], ["fb", "Facebook", "f"], ["ig", "Instagram", "◙"], ["li", "LinkedIn", "in"],
  ["yt", "YouTube", "▶"], ["tt", "TikTok", "♪"], ["wa", "WhatsApp", "✆"], ["th", "Threads", "@"],
];

export const BLOCK_DEF: Record<string, Record<string, unknown>> = { // ui-config (LOCAL PATCH, platform side, 2026-09-08 full resync: block default settings, not data)
  text: { align: "Left", size: "Body" },
  /* Container (stage 4). `cells` holds the nested blocks; ratio/gap/stackMobile
     mirror the landing builder's row block, so the two read the same. */
  columns: { ratio: "1-1", gap: 20, stackMobile: true },
  notice: { tone: "Neutral", icon: true, align: "Left" },
  button: { style: "Primary", linkKind: "URL", href: "", page: "Pick a page…", full: false },
  image: { ratio: "Wide 16:9", caption: "", round: true, alt: "", src: "" },
  hero: { sub: "Everything you need for your visit, in one place.", cta: "Learn more", bg: "Gradient", height: "Standard", img: "" },
  video: { url: "", caption: "", poster: true },
  gallery: { count: "3", layout: "Grid", captions: false, urls: "" },
  map: { address: "", zoom: "Building", directions: true },
  faq: { text2: "Do I need an appointment?", expand: false, a1: "Most visits take 10–20 minutes.", a2: "No — walk in any time we're open, or book ahead if you'd rather not queue." },
  quote: { who: "Customer", photo: false },
  stats: { s1v: "4.9★", s1l: "service rating", s2v: "8 min", s2l: "avg wait", s3v: "12", s3l: "departments" },
  countdown: { label: "Opens in", units: "Hours / min / sec" },
  social: { x: true, fb: true, li: true, yt: true, ig: false, tt: false, wa: false, th: false, xUrl: "", fbUrl: "", liUrl: "", ytUrl: "", igUrl: "", ttUrl: "", waUrl: "", thUrl: "" },
  /** Reads start/end/location live from the event record — never authored here.
   *  Config controls only which pieces show. */
  eventdetails: { showDate: true, showLocation: true, showAddToCalendar: true, layout: "Inline" },
  divider: { style: "Solid", space: "Normal" },
  spacer: { height: "Medium" },
};

export type CfgRow = [string, string, string, string[]?, boolean?];
export const BLOCK_CFG: Record<string, CfgRow[]> = {
  text: [["text", "Text", "area"], ["align", "Alignment", "sel", ["Left", "Center", "Right"]], ["size", "Size", "sel", ["Small", "Body", "Large"]]],
  columns: [["ratio", "Column ratio", "sel", ["1-1", "2-1", "1-2"]], ["gap", "Gap (px)", "line"], ["stackMobile", "Stack on mobile", "tog"]],
  notice: [["text", "Message", "area"], ["align", "Alignment", "sel", ["Left", "Center", "Right"]], ["tone", "Tone", "sel", ["Neutral", "Info", "Warning", "Success"]], ["icon", "Show icon", "tog"]],
  button: [["text", "Label", "line"], ["style", "Style", "sel", ["Primary", "Secondary", "Text link"]], ["full", "Full width", "tog"]],
  image: [["src", "Image URL", "line"], ["alt", "Alt text", "line"], ["ratio", "Shape", "sel", ["Wide 16:9", "Square", "Tall 4:5", "Banner 3:1"]], ["caption", "Caption", "line"], ["round", "Rounded corners", "tog"]],
  hero: [["text", "Headline", "area"], ["sub", "Subhead", "line"], ["cta", "Button label", "line"], ["img", "Background image URL", "line"], ["bg", "Background", "sel", ["Gradient", "Solid brand", "Photo"]], ["height", "Height", "sel", ["Compact", "Standard", "Tall"]]],
  video: [["url", "Video URL", "line"], ["caption", "Caption", "line"], ["poster", "Show poster frame", "tog"]],
  gallery: [["urls", "Image URLs — one per line", "area"], ["count", "Images", "sel", ["2", "3", "4", "6"]], ["layout", "Layout", "sel", ["Grid", "Carousel"]], ["captions", "Show captions", "tog"]],
  map: [["address", "Address", "line"], ["zoom", "Zoom", "sel", ["Building", "Campus", "City"]], ["directions", "Show directions link", "tog"]],
  faq: [["text", "First question", "line"], ["a1", "First answer", "area"], ["text2", "Second question", "line"], ["a2", "Second answer", "area"], ["expand", "Show answers expanded", "tog"]],
  quote: [["text", "Quote", "area"], ["who", "Attribution", "line"], ["photo", "Show avatar", "tog"]],
  stats: [["s1v", "Stat 1 — value", "line"], ["s1l", "Stat 1 — label", "line"], ["s2v", "Stat 2 — value", "line"], ["s2l", "Stat 2 — label", "line"], ["s3v", "Stat 3 — value", "line"], ["s3l", "Stat 3 — label", "line"]],
  countdown: [["label", "Label", "line"], ["units", "Show", "sel", ["Hours / min / sec", "Days / hours / min"]]],
  social: [],
  eventdetails: [["showDate", "Show date & time", "tog"], ["showLocation", "Show location", "tog"], ["showAddToCalendar", "Show add-to-calendar button", "tog"], ["layout", "Layout", "sel", ["Inline", "Card"]]],
  divider: [["style", "Style", "sel", ["Solid", "Dashed", "Dotted"]], ["space", "Spacing", "sel", ["Tight", "Normal", "Loose"]]],
  spacer: [["height", "Height", "sel", ["Small", "Medium", "Large"]]],
};

export const CORE_DEF: Record<string, Record<string, unknown>> = {
  wait: { cta: "Join the line", altText: "Rather book an appointment instead?", altUrl: "" },
  contact: { cta: "Join the line" },
  csat: { cta: "Take the survey" },
  apptconfirm: { cta: "Add to calendar", reschedText: "Need to reschedule?", reschedUrl: "" },
  register: { cta: "Save my spot" },
  tickets: { cta: "Checkout" },
};
export const CORE_CFG: Record<string, CfgRow[]> = {
  wait: [["cta", "Button label", "line"], ["altText", "Secondary link text", "line"], ["altUrl", "Secondary link URL", "line"]],
  contact: [["cta", "Button label", "line"]],
  csat: [["cta", "Button label", "line"]],
  apptconfirm: [["cta", "Calendar button label", "line"], ["reschedText", "Reschedule link text", "line"], ["reschedUrl", "Reschedule link URL", "line"]],
  register: [["cta", "Button label", "line"]],
  tickets: [["cta", "Checkout button label", "line"]],
};

/* Type tints, glyphs and hub copy. */
export const TYPE_TINT: Record<string, [string, string]> = {
  Queue: ["#FAF1DE", "#8A5B12"], Mixed: ["#EDE5FE", "#5142C7"], Pages: ["#F4F5F7", "#697180"],
  Appointment: ["#E0F2F1", "#0AA396"], Event: ["#FDEBF3", "#D61A55"], Experience: ["#E4E1F7", "#262265"],
  Board: ["#E8EEFD", "#2D60EB"],
};
export const TYPE_GLYPH: Record<string, string> = { Queue: "☰", Mixed: "▤", Pages: "▤", Appointment: "◔", Event: "◆", Experience: "◈", Board: "▣" };
export const HUB_VERB: Record<string, string> = { Queue: "Join — ", Appointment: "Book — ", Event: "Register — ", Experience: "Enter — ", Mixed: "Open — ", Pages: "Open — ", Board: "Open — " };

/** Type of a document — its declared type, else inferred from its page mix. */
export function docType(d: Doc): string {
  if (d.type) return d.type;
  const A: Record<string, number> = { apptsvc: 1, appttime: 1, apptconfirm: 1 };
  const EV: Record<string, number> = { register: 1, tickets: 1 };
  const kinds = new Set(d.pages.filter((x) => x.id !== "content" && x.id !== "contact" && x.id !== "exphub").map((x) => (A[x.id] ? "a" : EV[x.id] ? "e" : "q")));
  if (kinds.size > 1) return "Mixed";
  if (kinds.has("a")) return "Appointment";
  if (kinds.has("e")) return "Event";
  if (kinds.has("q")) return d.pages.some((x) => x.id === "content") ? "Mixed" : "Queue";
  return "Pages";
}

let _seq = 0;
export const njUid = (prefix: string) => `${prefix}${Date.now().toString(36)}-${++_seq}`;

export function mkPage(typeId: string, label?: string, over: Partial<NJPage> = {}, journeyType?: string): NJPage {
  const t = regOf(typeId);
  const page: NJPage = {
    id: t.id, uid: njUid("pg"), label: label || t.label, color: t.color, tint: t.tint, core: t.core,
    h: t.seed.h, s: t.seed.s, toggles: t.seed.toggles.map((x) => ({ ...x })), content: [], ...over,
  };
  /* A confirmation page is born with the sections its journey type can use, and
     the off-by-default ones already off (2026-08-31). Which sections apply is a
     property of the TYPE, not of the page — see confirmation-sections.ts. */
  if ((typeId === "confirmation" || typeId === "apptconfirm") && !page.sections) {
    page.sections = csDefaultState(csTypeOf(journeyType ?? (typeId === "apptconfirm" ? "Appointment" : undefined)));
    if (page.bannerTitle === undefined) page.bannerTitle = "You're all set";
  }
  /* Hero on by default, every page type (operator 2026-08-24) — seeded here so
   * derived documents and newly added pages both get one; removable per page. */
  if (typeId !== "board" && !page.content.some((c) => c.type === "hero")) {
    page.content = hoistHero([{ uid: njUid("bl"), type: "hero", text: t.seed.h, sub: t.seed.s || BLOCK_DEF.hero!.sub }, ...page.content]);
  }
  return page;
}

/** Hero always sits above every other block, whatever order things arrived in. */
export function hoistHero(list: Block[]): Block[] {
  const hero = list.filter((c) => c.type === "hero");
  return hero.length ? [...hero, ...list.filter((c) => c.type !== "hero")] : list;
}

/** Seed the attachment block each page type carries (never duplicated). */
export function seedAttachments(d: Doc): Doc {
  let touched = false;
  const pages = d.pages.map((p) => {
    const type = ATTACH_FOR[p.id];
    if (!type || p.content.some((c) => c.type === type)) return p;
    touched = true;
    return { ...p, content: p.content.concat([{ uid: njUid("at"), type }]) };
  });
  return touched ? { ...d, pages } : d;
}

/** A page step addresses a page by its INDEX in `doc.pages`, stringified — the
 *  same addressing the document has always used. `pageKey` / `pageIdx` in
 *  journey-pages.tsx are the pair that knows; inlined here because that module
 *  imports this one. */
const pgKey = (i: number) => String(i);

/** A derived journey's opening flow: the page spine, then whatever comms/waits
 *  the owning object implies, then the outcome. `trigger` is the vocabulary id
 *  the journey's type starts on. */
function flow(n: number, extra: QFStep[], exit: string, trigger = "screen_services", confirmIdx?: number): QFFlow {
  const steps: QFStep[] = [];
  for (let i = 0; i < n; i++) steps.push(qfMkPage(pgKey(i), {}));
  extra.forEach((x) => steps.push(x));
  /* Immediately before the End: the confirmation page is the last thing the
     customer sees, and the End records the OUTCOME, which is a different thing.
     Collapsing the two would lose the outcome vocabulary the flow depends on
     (operator ruling, 2026-08-31). */
  if (confirmIdx != null) steps.push(qfMkPage(pgKey(confirmIdx), {}));
  steps.push(qfMkExit(exit));
  return { trigger: { event: trigger }, steps };
}

/* ── Derivation from the tenant's real objects ─────────────────────────
 * Each derived document keeps a stable key so an overlay written in the
 * designer reattaches to the same object on the next load, and an `origin` so
 * the designer can open the object that owns the journey.
 */

/** The owning-record audit fields every derived row's fallback reads. Optional
 *  on all four owner shapes because no op returns them yet — the ask is
 *  `CD-API-REQUEST-journey-audit-2026-08-27.md`; `ownerAudit` reads whatever
 *  arrives (camel or snake, actor as string or object) and nothing when none. */
export interface OwnerAuditFields {
  createdAt?: string; createdBy?: string | { id?: string; name?: string; email?: string };
  updatedAt?: string; updatedBy?: string | { id?: string; name?: string; email?: string };
}
export interface QueueLike extends OwnerAuditFields {
  id: string; name: string; status: string; locationName?: string; services?: string[];
  joinCode?: string; avgWaitMins?: number; waiting?: number; journey?: unknown;
}
/** Everything the queue editor configures, in the shape Setup writes.
 *  Mirrors QueueSummary's editable half (queues-api) — same field names, so a
 *  patch built here goes straight to updateQueue. */
export interface QueueConfigLike {
  id: string; name: string; typeId: string; status?: string;
  locationId?: string; locationName?: string;
  ticketPrefix?: string; slaTargetMins?: number; needed?: number;
  hours?: { days: number[]; start: string; end: string }[];
  /** Set by the hours step's own mutations only — see `qAuthorHours` in
   *  NewJourneys. Carried on the draft so both write bodies can post it. */
  hoursAuthored?: boolean;
  /** Take joins OUTSIDE opening hours. Lifts both the building's hours and the
   *  desk's own; the desk's status (open / paused / closed) still governs.
   *  Deliberately NOT part of `hoursAuthored` — it is not a schedule, it is the
   *  absence of one, and setting it must not claim the author wrote hours. */
  ignoreHours?: boolean;
  assignedStaff?: { id: string; name: string; role?: string | null }[];
  serviceSelection?: { type: "category" | "service"; id: string }[];
  /* Staffing and Resources use the SAME group-or-item picker as Services, and
     both were being written onto this object by the Setup panel while the type
     did not declare them (added 2026-09-08). Undeclared meant the patch built
     here could not go straight to `updateQueue` — which is the one thing this
     interface's own note promises. Shape mirrors `serviceSelection`: "group"
     rather than "category", because a staff group is not a service category. */
  staffSelection?: { type: "group" | "item"; id: string }[];
  resourceSelection?: { type: "group" | "item"; id: string }[];
  services?: string[]; resources?: string[];
  heroImage?: string | null; journeySource?: string; joinCode?: string;
  /** Which library message flow runs for this queue (Setup · Messaging). */
  messagingFlowId?: string;
}
/** A display board's configuration — MessageBoard's editable half. */
export interface BoardConfigLike {
  id: string; name: string; queueIds: string[]; locationId?: string | null;
  layout?: "now-serving" | "full-list"; theme: "light" | "dark";
  showWaitEstimates: boolean; nameFormat: "full" | "first-initial" | "first" | "ticket";
  message: string; blocks: { uid: string; type: string; cfg: Record<string, unknown> }[];
  updatedAt?: string;
}
export function docFromQueue(q: QueueLike): { key: string; doc: Doc } {
  const key = `queue:${q.id}`;
  const status = q.status === "open" ? "Live" : q.status === "paused" ? "Paused" : "Draft";
  const svc = (q.services ?? []).slice();
  const where = q.locationName ? `${q.locationName} · ` : "";
  const doc: Doc = {
    name: q.name, type: "Queue", status, updated: "—", edited: false, audit: ownerAudit(q),
    sub: `${where}${svc.length} service${svc.length === 1 ? "" : "s"}${q.joinCode ? ` · join code ${q.joinCode}` : ""}`,
    origin: { kind: "queue", id: q.id, label: q.name, route: "queues.console", params: { id: q.id } },
    pages: [
      mkPage("services", undefined, { svc, s: "Pick a service — we'll estimate your wait." }),
      mkPage("wait", undefined, { s: q.locationName ? `Walk-in service · ${q.locationName}` : "Walk-in service" }),
      mkPage("contact"),
      mkPage("waiting"),
      mkPage("csat"),
    ],
    flow: (() => {
      const dec = qfMkDecision();
      dec.condition = "Was the customer served?";
      /* No confirmation page on either path (operator, 2026-09-02): "Waiting in
         queue" is the queue's confirmation, so the pages here run
         services → wait → contact → waiting → csat and each branch ends on its
         own outcome.
         This retires the per-path `confirm()` added on 2026-09-01 — its purpose
         was that a customer who LEFT the queue still reached an end page with a
         banner stating the outcome. That reasoning still holds and is now the
         End step's own job: `qfMkExit` carries the outcome vocabulary, which is
         what the banner read anyway. */
      dec.paths = [
        { key: "yes", label: "Yes", steps: [qfMkPage(pgKey(4), {}), qfMkSurvey(null, "Post-visit survey"), qfMkExit("Served")] },
        { key: "no", label: "No", steps: [{ ...qfMkComms(null, "Sorry we missed you"), promo: true }, qfMkExit("Left the queue")] },
      ];
      return flow(4, [qfMkComms(null, "You're up next"), dec], "Served");
    })(),
  };
  return { key, doc: seedAttachments(doc) };
}

export interface GuestEntry { firstName: string; lastName?: string; email: string; kind?: "person" | "segment"; count?: number }export interface EventLike extends OwnerAuditFields {
  id: string; title: string; status: string; date?: string; time?: string; location?: string;
  ticketTypeIds?: string[]; isPaid?: boolean; description?: string;
  bookingQuestions?: { kind: string; id: string }[]; surveys?: string[];
  /** Coupons accepted at checkout for this event (operator ruling, 2026-09-02).
   *  Event-scoped, on the record — a journey used to hold one, which meant two
   *  journeys against the same event could offer different codes and the event
   *  knew about neither. Flat, not per method: an event has one way in. */
  coupons?: string[];
  /** Automatic promotions, applied with no code entered. A DIFFERENT incentive
   *  from a coupon (operator, 2026-09-02) and a separate field for that reason:
   *  the promos library types them apart, and merging them here would lose which
   *  kind an id is at the moment it matters — checkout. */
  promos?: string[];
  /** Present once fetched from the real record (nj-binding.ts loadEventDoc) —
   *  docFromEvent doesn't read these, but requiredEventChecks() does, so a
   *  live event carries enough to compute readiness before Setup views exist. */
  method?: { inPerson?: boolean; virtual?: boolean };
  visibility?: string; virtualProvider?: string; start?: string; end?: string;
  locationId?: string; venueName?: string; address?: string;
  ticketGroupIds?: string[]; tickets?: unknown[]; capacity?: number | null;
  waitlist?: boolean; guestList?: GuestEntry[];
  /** Page-scope per the ownership map (register page's panel, not Setup) —
   *  still record fields, still written through updateEventRegistration. */
  approvalMode?: boolean; maxGuests?: number | null;
  messagingFlowId?: string;
  /** Multi-day events: per-day hours keyed by ISO date (YYYY-MM-DD). */
  dayHours?: Record<string, { start?: string; end?: string }>;
  /** "Custom…" recurrence: explicit ISO dates the event repeats on. */
  customDates?: string[];
  registrationOpens?: string; registrationCloses?: string;
  standardFields?: string[]; consent?: { marketing?: boolean; contactSharing?: boolean };
  redirectUrl?: string; termsCopy?: string;
  /** Stage 3B additions. */
  heroImage?: string; allDay?: boolean; repeatIndex?: number; timezone?: string;
  /** Per-type allocation: how many of the event's total tickets each selected
   *  type may issue. Partial allocation is legal — the remainder stays unassigned. */
  ticketTypeConfigs?: Record<string, { qty?: number; kind?: string; price?: number }>;
  /** Fidelity-pass additions — match the wizard's own fields. */
  tags?: string[]; locationMode?: "saved" | "custom";
}
export function docFromEvent(e: EventLike): { key: string; doc: Doc } {
  const key = `event:${e.id}`;
  const ticketed = !!(e.isPaid || (e.ticketTypeIds && e.ticketTypeIds.length));
  const status = /live|published|open/i.test(e.status) ? "Live" : /cancel/i.test(e.status) ? "Cancelled" : "Draft";
  const when = [e.date, e.time].filter(Boolean).join(" · ");
  const welcome = mkPage("content", "Welcome", {
    h: e.title, s: when || (e.description ?? ""),
    content: [{ uid: njUid("bl"), type: "eventdetails" }],
  });
  const pages = [
    welcome,
    mkPage("register", undefined, { s: ticketed ? "Registration takes a minute." : "Free — registration takes a minute." }),
  ];
  if (ticketed) pages.push(mkPage("tickets"));
  /* Events had no confirmation page at all until 2026-08-31 — a registrant
     reached the end of the flow and was shown nothing that named the event. */
  pages.push(mkPage("confirmation", undefined, {}, "Event"));
  const doc: Doc = {
    name: e.title, type: "Event", status, updated: "—", edited: false, audit: ownerAudit(e),
    sub: [when, e.location, ticketed ? "ticketed" : "free"].filter(Boolean).join(" · "),
    origin: { kind: "event", id: e.id, label: e.title, route: "events.detail", params: { id: e.id } },
    entryUid: welcome.uid,
    attach: {
      groupId: e.bookingQuestions?.find((b) => b.kind === "group")?.id ?? null,
      surveyId: e.surveys?.[0] ?? null,
    },
    pages,
    flow: flow(pages.length - 1, [qfMkComms(null, "Registration confirmed")], "Registered only", "registered", pages.length - 1),
  };
  return { key, doc: seedAttachments(doc) };
}

/** What an appointment funnel configures about itself. Deliberately small: the
 *  catalogue owns duration, buffers, windows, questions, surveys, promos and
 *  flows per service, and this holds only what is true of the FUNNEL. */
export interface ApptSetupLike {
  /** Ordered service selection — the order here is the order on the page. */
  serviceSelection: string[];
  groupByCategory: boolean;
  /** Locations this funnel offers. Empty = every location the services allow. */
  locationIds: string[];
  /** Delivery methods exposed, ceilinged by each service's own channels. */
  methods: { inPerson: boolean; phone: boolean; video: boolean };
  /** About-you page: what is asked beyond the service's question group. */
  standardFields: string[];
  consent: { marketing?: boolean; contactSharing?: boolean };
  policyUrl?: string;
  termsCopy?: string;
  redirectUrl?: string;
  /** Fallback flow for services that name none, and for non-service events. */
  messagingFlowId?: string;
  /** Legacy journey-wide attachments, kept read-only until removed (2026-08-31
   *  ruling). Not settable any more; shown so a live value is never invisible. */
  legacySurveyId?: string | null;
  legacyPromoId?: string | null;
}

export interface ApptGroupLike extends OwnerAuditFields {
  id: string; name: string; serviceIds: string[]; slug?: string | null;
}
export function docFromApptGroup(g: ApptGroupLike): { key: string; doc: Doc } {
  const key = `appt:${g.id}`;
  const doc: Doc = {
    name: `${g.name} appointments`, type: "Appointment", status: "Live", updated: "—", edited: false, audit: ownerAudit(g),
    sub: `${g.serviceIds.length} bookable service${g.serviceIds.length === 1 ? "" : "s"} · live availability`,
    origin: { kind: "appointment", id: g.id, label: g.name, route: "ops.catalogue", slug: g.slug ?? null },
    pages: [
      mkPage("apptsvc", "Book a service", { svc: g.serviceIds.slice() }),
      mkPage("appttime"),
      mkPage("contact", "About you", { h: "About you", s: "We'll email your confirmation and reminders." }),
      mkPage("apptconfirm"),
    ],
    flow: flow(4, [
      qfMkComms(null, "Booking confirmed"),
      { ...qfMkWait(), amount: 24, unit: "Hours", timing: "before_appt" },
      qfMkComms(null, "Appointment reminder"),
    ], "Attended", "booked"),
  };
  return { key, doc: seedAttachments(doc) };
}

/* ── Display boards ────────────────────────────────────────────────────
 * A board is another form of journey: one page (the lobby display), no
 * customer input, and a configuration that lives in the boards library. */
export interface BoardLike extends OwnerAuditFields {
  id: string; name: string; queueIds: string[]; theme?: string; message?: string;
  queueNames?: string[]; updatedAt?: string;
}
export function docFromBoard(b: BoardLike): { key: string; doc: Doc } {
  const key = `board:${b.id}`;
  const shown = b.queueNames?.length
    ? b.queueNames.join(" · ")
    : `${b.queueIds.length} queue${b.queueIds.length === 1 ? "" : "s"}`;
  const page = mkPage("board", "Display Board", { s: b.message || "Live from the queues this board shows." });
  const doc: Doc = {
    name: b.name || "Untitled board", type: "Board", status: "Live", updated: "—", edited: false, audit: ownerAudit(b),
    sub: [`Display board`, shown, b.theme ? `${b.theme} theme` : null].filter(Boolean).join(" · "),
    origin: { kind: "board", id: b.id, label: b.name || "Untitled board", route: "queues.boards" },
    entryUid: page.uid,
    pages: [page],
    flow: flow(1, [], "Served", "screen_services"),
  };
  return { key, doc };
}

/* ── Template documents (created in the designer, no owning object yet) ── */
export function templateDoc(kind: "queue" | "appt" | "event" | "onestop" | "board" | "blank"): { key: string; doc: Doc } {
  const key = `local:${njUid("nj")}`;
  if (kind === "queue") {
    const doc: Doc = {
      name: "Untitled queue journey", type: "Queue", status: "Draft", updated: "Just now", edited: false,
      sub: "Join → wait → contact → in line → CSAT",
      origin: { kind: "local", id: key, label: "Not attached to a queue yet" },
      /* No confirmation page (operator, 2026-09-02): "Waiting in queue" IS the
         queue's confirmation. A customer who joins lands on the page that shows
         their ticket and position — telling them "you're confirmed" and then
         moving them to the screen that proves it is one screen too many, and the
         one they need is the second.
         Other journey types keep theirs: a booking or a registration confirms
         something that happens LATER, so there is nothing to stand on afterwards
         and the confirmation is the whole receipt.
         `flow()`'s FIRST argument is the number of spine pages, not an index,
         and its FIFTH is the confirmation page's index. Five pages remain, so
         the count stays 5 and the confirmation index is dropped entirely —
         passing `4` would have re-added CSAT as the confirmation step and
         placed it after the comms, reordering the flow while appearing to fix
         it. */
      pages: [mkPage("services"), mkPage("wait"), mkPage("contact"), mkPage("waiting"), mkPage("csat")],
      flow: flow(5, [qfMkComms(null, "You're up next")], "Served", "screen_services"),
    };
    return { key, doc: seedAttachments(doc) };
  }
  if (kind === "appt") {
    const doc: Doc = {
      name: "Untitled appointment journey", type: "Appointment", status: "Draft", updated: "Just now", edited: false,
      sub: "Service → time → contact → confirm",
      origin: { kind: "local", id: key, label: "Not attached to a catalogue yet" },
      pages: [mkPage("apptsvc"), mkPage("appttime"), mkPage("contact", "About you"), mkPage("apptconfirm")],
      flow: flow(4, [qfMkComms(null, "Booking confirmed")], "Attended", "booked"),
    };
    return { key, doc: seedAttachments(doc) };
  }
  if (kind === "event") {
    const welcome = mkPage("content", "Welcome", { content: [{ uid: njUid("bl"), type: "eventdetails" }] });
    const doc: Doc = {
      name: "Untitled event journey", type: "Event", status: "Draft", updated: "Just now", edited: false,
      sub: "Welcome → register → tickets",
      origin: { kind: "local", id: key, label: "Not attached to an event yet" },
      entryUid: welcome.uid,
      pages: [welcome, mkPage("register"), mkPage("tickets"), mkPage("confirmation", undefined, {}, "Event")],
      flow: flow(3, [qfMkComms(null, "Registration confirmed")], "Registered only", "registered", 3),
    };
    return { key, doc: seedAttachments(doc) };
  }
  if (kind === "onestop") {
    /* One journey, one page. The One-Stop page is the whole surface, which is
       why there is no flow to seed beyond the single step: a student does not
       walk through it, they arrive and choose. */
    const page = mkPage("onestop", "One-Stop page");
    const doc: Doc = {
      name: "Untitled One-Stop page", type: "One-Stop", status: "Draft", updated: "Just now", edited: false,
      sub: "One page — the desks, services and events a student can reach in one visit",
      origin: { kind: "local", id: key, label: "Not published to the portal yet" },
      entryUid: page.uid,
      pages: [page],
      flow: flow(1, [], "Served"),
    };
    return { key, doc };
  }
  if (kind === "board") {
    const page = mkPage("board", "Display Board");
    const doc: Doc = {
      name: "Untitled display board", type: "Board", status: "Draft", updated: "Just now", edited: false,
      sub: "Lobby display — pick the queues it shows",
      origin: { kind: "local", id: key, label: "Not saved to the boards library yet" },
      entryUid: page.uid,
      pages: [page],
      flow: flow(1, [], "Served"),
    };
    return { key, doc };
  }
  const first = mkPage("content", "Content", { content: [{ uid: njUid("c"), type: "text", text: "Add your copy here — pick more blocks from the palette." }] });
  const doc: Doc = {
    name: "Untitled journey", type: "Pages", status: "Draft", updated: "Just now", edited: false,
    sub: "Blank — pages picked from the type registry",
    origin: { kind: "local", id: key, label: "Standalone journey" },
    pages: [first],
    flow: flow(1, [], "Served"),
  };
  return { key, doc };
}
